import { Plugin } from "@ckeditor/ckeditor5-core";
import {
  toWidget,
  viewToModelPositionOutsideModelElement,
} from "@ckeditor/ckeditor5-widget/src/utils";
import { Widget } from "@ckeditor/ckeditor5-widget";

import CreateMarksCommand from "./CreateMarksCommand";

export default class MarksEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  destroy() {
    this.editor.off("cde:createHighlight");
    this.editor.off("cde:setItem");
  }

  init() {
    const editor = this.editor;

    this._defineSchema();
    this._defineConverters();

    editor.commands.add("createMarks", new CreateMarksCommand(this.editor));

    if (editor.plugins.has("TrackChangesEditing")) {
      const trackChangesEditing = editor.plugins.get("TrackChangesEditing");
      trackChangesEditing.enableCommand("createMarks");
    }

    editor.on("cde:setItem", (_eventInfo, ...args) => {
      editor.config.set("itemID", args[0]);
    });

    editor.editing.mapper.on(
      "viewToModelPosition",
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        ["mark"].includes(viewElement.name),
      ),
    );
  }

  _defineConverters() {
    const editor = this.editor;
    const conversion = editor.conversion;

    const createInlineMarkView = (modelElement, viewWriter) => {
      const uuid = modelElement.getAttribute("data-uuid");

      return viewWriter.createEmptyElement("mark", {
        "data-uuid": uuid,
      });
    };

    const createInlineMarkContainerView = (modelElement, viewWriter) => {
      const uuid = modelElement.getAttribute("data-uuid");

      const itemPointRefView = viewWriter.createContainerElement("mark", {
        "data-uuid": uuid,
      });
      const content = viewWriter.createText(`*`);
      viewWriter.insert(
        viewWriter.createPositionAt(itemPointRefView, 0),
        content,
      );
      return itemPointRefView;
    };

    // iprHighlight attribute conversions
    conversion.for("editingDowncast").attributeToAttribute({
      model: "iprActiveHighlight",
      view: {
        key: "class",
        value: "highlight active",
      },
    });

    conversion.for("editingDowncast").attributeToAttribute({
      model: "title",
      view: (modelAttributeValue) => ({
        key: "title",
        value: modelAttributeValue,
      }),
    });

    conversion.for("editingDowncast").attributeToAttribute({
      model: "iprHighlight",
      view: {
        key: "class",
        value: "highlight",
      },
    });

    // cdeHighlight attribute conversions.
    // The value is an object ({ active, title }) so a single attribute encodes
    // both the highlight title and whether it should render active (yellow) or
    // inactive (green). Collapsing the previous cdeHighlight/cdeActiveHighlight
    // pair into one attribute guarantees each text range resolves to exactly
    // one span/color — overlapping and nested highlights can no longer stack
    // two spans (whose visible color depended on view nesting order) or leak
    // state between sibling highlights.
    conversion.for("editingDowncast").attributeToElement({
      model: "cdeHighlight",
      view: (modelAttributeValue, conversionApi) => {
        const { writer } = conversionApi;
        const { active = false, title = "" } = modelAttributeValue || {};

        return writer.createAttributeElement("span", {
          title,
          class: active ? "highlight active" : "highlight",
        });
      },
    });

    // Inline Mark Element conversions
    conversion.for("dataDowncast").elementToElement({
      model: "inlineMarkElement",
      view: (modelElement, { writer: viewWriter }) => {
        return createInlineMarkView(modelElement, viewWriter);
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "inlineMarkElement",
      view: (modelElement, { writer: viewWriter }) => {
        return createInlineMarkView(modelElement, viewWriter);
      },
    });
    conversion.for("upcast").elementToElement({
      model: (viewElement, { writer: modelWriter }) => {
        const uuid = viewElement.getAttribute("data-uuid");

        if (viewElement.childCount === 1) {
          return modelWriter.createElement("itemPointReference", {
            "data-uuid": uuid,
          });
        } else {
          return modelWriter.createElement("inlineMarkElement", {
            "data-uuid": uuid,
          });
        }
      },
      view: "mark",
    });

    conversion.for("dataDowncast").elementToElement({
      model: "itemPointReference",
      view: (modelElement, { writer: viewWriter }) => {
        return createInlineMarkContainerView(modelElement, viewWriter);
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "itemPointReference",
      view: (modelElement, { writer: viewWriter }) => {
        const IPRElement = createInlineMarkContainerView(
          modelElement,
          viewWriter,
        );
        // TODO: reimplement widget
        return IPRElement;
      },
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.extend("$text", { allowAttributes: ["cdeHighlight", "title"] });
    schema.setAttributeProperties("cdeHighlight", {
      isFormatting: false,
      copyOnEnter: true,
    });

    schema.extend("paragraph", { allowAttributes: "data-uuid" });
    schema.setAttributeProperties("data-uuid", {
      isFormatting: false,
      copyOnEnter: false,
    });

    schema.register("inlineMarkElement", {
      allowAttributes: ["data-uuid"],
      allowWhere: "$text",
      isContent: true,
      isInline: true,
    });

    schema.register("itemPointReference", {
      allowAttributes: ["data-uuid", "cbt", "pbt", "title"],
      allowContentOf: "$text",
      allowWhere: "$text",
      isBlock: false,
      isContent: true,
      isInline: true,
    });
  }
}
